import {
  Cartesian3,
  Color,
  CustomDataSource,
  type Entity,
  HeightReference,
  HorizontalOrigin,
  NearFarScalar,
  PolygonHierarchy,
  PolylineGlowMaterialProperty,
} from "cesium";
import type { Feature, FeatureCollection, Position } from "geojson";
import { useCallback, useEffect, useRef, useState } from "react";
import { checkIfHSLA, hslaToHex } from "../../../../styles/helpers";
import type { GisDataItem } from "../mapBuilder/components/cesium/cesium-types";
import { createIconForFeature } from "../utils/cesiumIconUtils";

const GIS_DATA_SOURCE_NAME = "gisDataSource";

/**
 * Custom React hook for managing GIS data display in Cesium viewer
 * @param viewer - The Cesium viewer instance
 * @param gisDataArray - Array of GIS data items to display
 */
export function useCesiumGis(viewer: any, gisDataArray: GisDataItem[] | null) {
  const dataSourceRef = useRef<CustomDataSource | null>(null);
  const [fontLoaded, setFontLoaded] = useState(false);

  // Font loading detection effect
  useEffect(() => {
    let mounted = true;

    const waitForFont = async () => {
      await document.fonts.ready;
      await document.fonts.load("14px custom-icons-2");
      // First wait for document.fonts.ready
      if (document.fonts && document.fonts.ready) {
        try {
          await document.fonts.ready;
          console.log("Document fonts ready, now checking custom font");
        } catch (e) {
          console.warn("document.fonts.ready failed:", e);
        }
      }

      // Additional wait to ensure font is truly ready
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // More robust font checking with actual rendering test
      const checkFontWithRendering = (): boolean => {
        if (!document.fonts || !document.fonts.check) {
          return false;
        }

        try {
          // Check if font is loaded - match the CSS definition (font-weight: 200)
          const font14Ready = document.fonts.check("200 14px custom-icons-2");
          const font24Ready = document.fonts.check("200 24px custom-icons-2");

          if (!font14Ready || !font24Ready) {
            console.log("Font check failed - fonts not ready");
            return false;
          }

          // Create a test element to verify actual rendering
          const testElement = document.createElement("span");
          testElement.style.fontFamily = "custom-icons-2";
          testElement.style.fontSize = "14px";
          testElement.style.position = "absolute";
          testElement.style.left = "-9999px";
          testElement.style.visibility = "hidden";
          testElement.textContent = "\uE85E"; // Test icon

          document.body.appendChild(testElement);

          // Check if the element has proper dimensions (indicates font loaded)
          const rect = testElement.getBoundingClientRect();
          const hasProperDimensions = rect.width > 0 && rect.height > 0;

          document.body.removeChild(testElement);

          if (hasProperDimensions) {
            console.log(
              "Custom font verified as ready for GIS hook with rendering test",
            );
            return true;
          }
          console.log("Font rendering test failed - font not fully ready");
          return false;
        } catch (e) {
          console.warn("Font check with rendering failed:", e);
          return false;
        }
      };

      // Try checking up to 10 times with 500ms intervals
      for (let i = 0; i < 10; i++) {
        if (!mounted) return;

        if (checkFontWithRendering()) {
          if (mounted) {
            setFontLoaded(true);
          }
          return;
        }

        console.log(`Font check attempt ${i + 1}/10 failed, retrying...`);
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      // If we get here, font loading failed
      console.warn("Font loading failed after 10 attempts, proceeding anyway");
      if (mounted) {
        setFontLoaded(true);
      }
    };

    waitForFont();

    return () => {
      mounted = false;
    };
  }, []);

  // Get or create GIS data source
  const getOrCreateGisDataSource = useCallback(
    (viewer: any): CustomDataSource => {
      if (dataSourceRef.current) {
        return dataSourceRef.current;
      }

      let dataSource = viewer.dataSources.getByName(
        GIS_DATA_SOURCE_NAME,
      )[0] as CustomDataSource;

      if (!dataSource) {
        dataSource = new CustomDataSource(GIS_DATA_SOURCE_NAME);
        viewer.dataSources.add(dataSource);
      }

      dataSourceRef.current = dataSource;
      return dataSource;
    },
    [],
  );

  // Create label for GIS feature
  const createGisFeatureLabel = useCallback(
    (
      feature: Feature,
      position: any,
      dataSource: CustomDataSource,
      gisItem: GisDataItem,
    ) => {
      if (!feature.properties) return null;

      // Determine label text - priority: tooltipLabel > number > id
      let labelText = "";
      if (
        feature.properties.tooltipLabel &&
        feature.properties.tooltipLabel !== "none"
      ) {
        labelText = feature.properties.tooltipLabel;
      } else if (feature.properties.number) {
        labelText = feature.properties.number.toString();
      } else if (feature.id || feature.properties?.id) {
        const currentFeatureId = feature.id || feature.properties?.id;
        labelText = currentFeatureId.toString();
      }

      // Only create label if we have text to display
      if (!labelText) return null;

      // Determine label color - matching Mapbox integration logic
      let labelColor = Color.WHITE; // Default to white

      if (feature.properties.labelColor) {
        try {
          let colorToUse = feature.properties.labelColor;
          // Convert HSLA to hex for better Cesium compatibility
          if (checkIfHSLA(colorToUse)) {
            colorToUse = hslaToHex(colorToUse);
          }
          labelColor = Color.fromCssColorString(colorToUse);
        } catch (e) {
          if (feature.properties.color) {
            try {
              let colorToUse = feature.properties.color;
              if (checkIfHSLA(colorToUse)) {
                colorToUse = hslaToHex(colorToUse);
              }
              labelColor = Color.fromCssColorString(colorToUse);
            } catch (e2) {
              labelColor = Color.WHITE;
            }
          }
        }
      } else if (feature.properties.color) {
        try {
          let colorToUse = feature.properties.color;
          // Convert HSLA to hex for better Cesium compatibility
          if (checkIfHSLA(colorToUse)) {
            colorToUse = hslaToHex(colorToUse);
          }
          labelColor = Color.fromCssColorString(colorToUse);
        } catch (e) {
          labelColor = Color.WHITE;
        }
      }

      // Create label entity
      const labelEntity = dataSource.entities.add({
        position: position,
        label: {
          text: labelText,
          font: "bold 12px Nunito, sans-serif",
          pixelOffset: new Cartesian3(0, -25, 0),
          fillColor: labelColor,
          outlineColor: Color.WHITE,
          outlineWidth: 4, // Reduced from 4 to prevent white outline from covering colored text
          style: 2, // FILL_AND_OUTLINE
          verticalOrigin: 1, // BOTTOM
          horizontalOrigin: HorizontalOrigin.CENTER,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          scaleByDistance: new NearFarScalar(1.0e2, 1.0, 1.0e7, 0.5),
        },
      });

      return labelEntity;
    },
    [],
  );

  // Main effect to display GIS data
  useEffect(() => {
    if (!viewer || !gisDataArray || !fontLoaded) {
      return;
    }


    const dataSource = getOrCreateGisDataSource(viewer);
    dataSource.entities.removeAll(); // Clear existing entities

    const addedIdeaIds = new Set<string>();

    gisDataArray.forEach((gisItem, index) => {
      if (!gisItem || !gisItem.data || !gisItem.data.features) {
        return;
      }

      const featureCollection: FeatureCollection = gisItem.data;
      const layerType = gisItem.layerType || "default";

      // Define colors based on layer type or index
      const colors = [
        Color.BLUE,
        Color.GREEN,
        Color.RED,
        Color.YELLOW,
        Color.PURPLE,
        Color.ORANGE,
      ];
      let defaultColor = colors[index % colors.length];

      switch (layerType) {
        case "polygon":
          defaultColor = Color.BLACK.withAlpha(0.3);
          break;
        case "line":
          defaultColor = Color.BLACK;
          break;
        case "point":
          defaultColor = Color.BLACK;
          break;
      }

      featureCollection.features.forEach((feature: Feature) => {
        if (!feature.geometry) return;

        const ideaId = feature.properties?.ideaId;
        if (ideaId) {
          if (addedIdeaIds.has(ideaId)) {
            return; // Skip duplicate feature
          }
          addedIdeaIds.add(ideaId);
        }

        let positions: Position[] = [];
        const geometryType = feature.geometry.type;

        if (geometryType === "Polygon") {
          positions = feature.geometry.coordinates[0];
        } else if (geometryType === "LineString") {
          positions = feature.geometry.coordinates;
        } else if (geometryType === "Point") {
          positions = [feature.geometry.coordinates];
        } else {
          return; // Unsupported geometry type
        }

        const cesiumPositions = positions.map((coord) =>
          Cartesian3.fromDegrees(coord[0], coord[1], coord[2] || 0),
        );

        let entityColor = defaultColor;
        if (feature.properties?.color) {
          try {
            entityColor = Color.fromCssColorString(feature.properties.color);
          } catch (e) {
            /* ignore invalid color */
          }
        }

        let entity: Entity;
        const entityOptions = {
          properties: feature.properties ?? {},
        };

        // Calculate label position for all geometry types
        let labelPosition: any;

        switch (geometryType) {
          case "Polygon": {
            // Create polygon without outline
            entity = dataSource.entities.add({
              ...entityOptions,
              name: gisItem.layerName || `Polygon ${index}`,
              polygon: {
                hierarchy: new PolygonHierarchy(cesiumPositions),
                material: entityColor.withAlpha(0.3),
                outline: false,
                perPositionHeight: true,
              },
            });

            // Create separate polyline for the border
            const borderPositions = [...cesiumPositions, cesiumPositions[0]];
            dataSource.entities.add({
              ...entityOptions,
              name: (gisItem.layerName || `Polygon ${index}`) + " Border",
              polyline: {
                positions: borderPositions,
                width: 3,
                material: entityColor,
              },
            });

            // Calculate centroid for label position
            let totalX = 0;
            let totalY = 0;
            let totalZ = 0;
            cesiumPositions.forEach((pos) => {
              totalX += pos.x;
              totalY += pos.y;
              totalZ += pos.z;
            });
            labelPosition = new Cartesian3(
              totalX / cesiumPositions.length,
              totalY / cesiumPositions.length,
              totalZ / cesiumPositions.length,
            );
            break;
          }

          case "LineString": {
            entity = dataSource.entities.add({
              ...entityOptions,
              name: gisItem.layerName || `LineString ${index}`,
              polyline: {
                positions: cesiumPositions,
                width: 3,
                material: entityColor,
                clampToGround: true,
              },
            });

            // Use middle point for label position
            const middleIndex = Math.floor(cesiumPositions.length / 2);
            labelPosition = cesiumPositions[middleIndex];
            break;
          }

          case "Point": {
            const position = cesiumPositions[0];
            const hasIcon = feature.properties?.icon;

            if (!hasIcon) {
              entity = dataSource.entities.add({
                ...entityOptions,
                name: gisItem.layerName || `Point ${index}`,
                position: position,
                point: {
                  pixelSize: 12,
                  color: entityColor,
                  outlineColor: Color.WHITE,
                  outlineWidth: 1.5,
                  heightReference:
                    positions.length > 0 && positions[0].length > 2
                      ? HeightReference.NONE
                      : HeightReference.CLAMP_TO_GROUND,
                  disableDepthTestDistance: Number.POSITIVE_INFINITY,
                },
              });
            } else {
              // Create invisible entity for picking when icon is present
              entity = dataSource.entities.add({
                ...entityOptions,
                name: gisItem.layerName || `Point ${index}`,
                position: position,
                point: {
                  pixelSize: 1,
                  color: Color.TRANSPARENT,
                  show: false,
                },
              });
            }

            labelPosition = position;
            break;
          }

          default:
            return;
        }

        // Create label for the feature
        if (labelPosition) {
          createGisFeatureLabel(feature, labelPosition, dataSource, gisItem);

          // Create icon for the feature if it has an icon property
          if (feature.properties?.icon) {
            createIconForFeature({
              feature,
              position: labelPosition,
              viewer,
              entityManager: dataSource.entities,
            });
          }
        }
      });
    });

    // Monitor entity count to prevent WebGL buffer issues
    if (viewer && viewer.entities) {
      const entityCount = viewer.entities.values.length;
      if (entityCount > 1000) {
        console.warn(
          `High entity count detected: ${entityCount}. Consider data optimization.`,
        );
      }
    }
  }, [
    viewer,
    gisDataArray,
    fontLoaded,
    getOrCreateGisDataSource,
    createGisFeatureLabel,
  ]);

  // Cleanup function
  const clearGisData = useCallback(() => {
    if (dataSourceRef.current) {
      dataSourceRef.current.entities.removeAll();
    }
  }, []);

  // Effect for cleanup on unmount
  useEffect(() => {
    return () => {
      clearGisData();
    };
  }, [clearGisData]);

  return {
    clearGisData,
    dataSource: dataSourceRef.current,
  };
}
